import React, {useState, useEffect} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import GuestTableCell from "../../Common/GuestTableCell";
import {useHistory} from "react-router-dom";
import {endOfDay, isPast, parse} from "date-fns";
import {Grid, LinearProgress, Typography, useTheme} from "@mui/material";
import {styled} from "@mui/material/styles";
import PrimaryButton from "../../Common/PrimaryButton";
import GuestTableRow from "../../Common/GuestTableRow";
import {GuestsPropTypes} from "../../../utils/PropTypes/GuestsPropTypes";
import SecondaryButton from "../../Common/SecondaryButton";
import GuestItinerary from "./GuestItinerary";
import Accommodation from "./Accommodation";
import OutlineButton from "../../Common/OutlineButton";

const PREFIX = 'ReservedRoom';
const classes = {
    root: `${PREFIX}-root`,
    tableHeader: `${PREFIX}-tableHeader`,
    progressBar: `${PREFIX}-progressBar`,
    test: `${PREFIX}-test`,
}
const Root = styled('div')(({theme}) => ({
    [`&.${classes.root}`]: {
        marginTop: '50px'
    },
    [`& .${classes.tableHeader}`]: {
        fontSize: '1.4rem',
        fontWeight: 600,
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        textAlign: 'left',
    },
    [`& .${classes.progressBar}`]: {
        height: '10px'
    },
    [`& .${classes.test}`]: {
        color: theme.palette.common.white
    },
}));

const columns = [
    { id: 'index', label: 'Guest #', minWidth: 100, align: 'center' },
    { id: 'sex', label: 'Sex', minWidth: 100 },
    {
        id: 'accommodation',
        label: 'Accommodation',
        minWidth: 200,
        align: 'left',
    },
    {
        id: 'name',
        label: 'Guest Name',
        minWidth: 170,
        align: 'center',
    },
    {
        id: 'progress',
        label: 'Guest Form',
        minWidth: 170,
        align: 'center',
    },
    {
        id: 'update',
        label: '',
        minWidth: 170,
        align: 'center',
    },
];

const responsiveColumns = [
    { id: 'index', label: 'Guest #', minWidth: 100, align: 'center' },
    {
        id: 'update',
        label: '',
        minWidth: 170,
        align: 'center',
    },
    { id: 'sex', label: 'Sex', minWidth: 100 },
    {
        id: 'accommodation',
        label: 'Accommodation',
        minWidth: 200,
        align: 'left',
    },
    {
        id: 'name',
        label: 'Guest Name',
        minWidth: 170,
        align: 'center',
    },
    {
        id: 'progress',
        label: 'Guest Form',
        minWidth: 170,
        align: 'center',
    },
];

const GuestList = ({guests, bookingData, payments}) => {
    const history = useHistory();
    const theme = useTheme();
    const [responsive, setResponsive] = useState(false);
    const [cols, setCols] = useState(columns);

    useEffect(() => {
        checkResponsive();
        window.addEventListener('resize', checkResponsive, true);
    }, []);

    useEffect(() => {
        if (responsive)
            setCols(responsiveColumns);
        else
            setCols(columns);
    }, [responsive]);


    const checkResponsive = () => {
        const width = window.innerWidth;
        if (width < theme.breakpoints.values.md) setResponsive(true);
        else setResponsive(false);
    };


    const tripDate = endOfDay(parse(bookingData.tripDate, 'yyyy-MM-dd', new Date()));
    const isPreviousTrip = isPast(tripDate);

    const updateGuestForm = (guestId) => {
        history.push(`/booking/guest/${guestId}/personal-information`);
    };

    const getGuestSex = (value) => {
        if (value === 'M') return 'Male';
        if (value === 'F') return 'Female';

        return '-';
    };

    const createData = (index, guest) => {
        // const density = population / size;
        const id = (<p>guest.guestFk</p>);
        const sex = (<p>
            {getGuestSex(guest.guestInfo.sex)}
        </p>);
        const accommodation = (<p>
            {guest['accommodation']['roomNameTitle']} {guest.isSingle &&
            <span style={{fontWeight: 400, fontSize: '10px'}}>(Single occupancy)</span>}
        </p>);
        const name = (<p>
            {guest.guestInfo.firstName ? `${guest.guestInfo.firstName} ${guest.guestInfo.middleName || ''} ${guest.guestInfo.lastName || ''}` : '-'}
        </p>)
        const progress = (<LinearProgress className={classes.progressBar} variant="determinate"
                                         value={guest.percentage}/>);

        const update = (!isPreviousTrip && <PrimaryButton
            onClick={() => updateGuestForm(guest.guestFk)}>{guest.percentage < 100 ? 'Complete' : 'Update'}</PrimaryButton>);

        return { id, index, sex, accommodation, name, progress, update };
    }

    const rows = guests.map((g, i) => createData(i + 1, g));
    const goToBoatLayout = () => {
        history.push(`/booking/my-bookings/${bookingData.id}/select-accommodation`);
    }

    return (
        <Root className={classes.root}>
            <Grid container spacing={3} sx={{px: {xs: '1.6rem', lg: '0'}}}>
                <Grid item xs={12} md={6}>
                    <Typography variant={"h3"} sx={{ml: {xs: '1.6rem', lg: '0'}}}>Guests</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                    {bookingData.trip.trip_class_info.tripType!=='shorebased' && <SecondaryButton sx={{
                        ':disabled': {backgroundColor: 'rgba(0, 0, 0, 0.26)', color: '#565656'},
                        float: 'right',
                        width: '280px'
                    }} onClick={goToBoatLayout} disabled={!Object.values(payments).some(obj => obj.paid === true)}>Select
                        Accommodation</SecondaryButton>}
                    {/* <SecondaryButton sx={{':disabled': {backgroundColor: 'rgba(0, 0, 0, 0.26)', color: '#565656'}, float:'right',width:'280px'}} onClick={goToBoatLayout}>Change Accommodation</SecondaryButton> */}
                </Grid>
            </Grid>


            <TableContainer sx={{ maxHeight: 440, marginTop: '20px' }}>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                        <TableRow>
                            {cols.map((column) => (
                                <GuestTableCell
                                    key={column.id}
                                    align={'center'}
                                    style={{ minWidth: column.minWidth }}
                                >
                                    <Typography variant={"h3"} className={classes.tableHeader}>
                                        {column.label}
                                    </Typography>
                                </GuestTableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => {
                                return (
                                    <GuestTableRow role="checkbox" tabIndex={-1} key={row.index}>
                                        {cols.map((column) => {
                                            const value = row[column.id];
                                            return (
                                                <GuestTableCell key={column.id + '-' + row.id} align={column.align}>
                                                    {value}
                                                </GuestTableCell>
                                            );
                                        })}
                                    </GuestTableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Root>
    );
}

export default GuestList;

GuestList.propTypes = {
    guests: GuestsPropTypes,
};